import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, BookOpen, CheckCircle, Clock, Moon, Sun, LogOut, Settings, GraduationCap, FileCheck } from 'lucide-react';

const DashboardAdmin = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/reportes/dashboard-admin`,
          { withCredentials: true }
        );
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const statCards = [
    { title: 'Total Usuarios', value: stats?.total_usuarios || 0, icon: Users, color: 'text-blue-600 dark:text-blue-400' },
    { title: 'Docentes', value: stats?.total_docentes || 0, icon: GraduationCap, color: 'text-green-600 dark:text-green-400' },
    { title: 'Estudiantes', value: stats?.total_estudiantes || 0, icon: Users, color: 'text-purple-600 dark:text-purple-400' },
    { title: 'Total Actividades', value: stats?.total_actividades || 0, icon: BookOpen, color: 'text-orange-600 dark:text-orange-400' },
    { title: 'Actividades Aprobadas', value: stats?.actividades_aprobadas || 0, icon: CheckCircle, color: 'text-teal-600 dark:text-teal-400' },
    { title: 'En Revisión', value: stats?.actividades_en_revision || 0, icon: Clock, color: 'text-yellow-600 dark:text-yellow-400' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard Administrativo</h1>
            <p className="text-sm text-muted-foreground">Bienvenido, {user?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-button"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8" data-testid="stats-grid">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="pb-3">
                  <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-1/3"></div>
                </CardHeader>
              </Card>
            ))
          ) : (
            statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="border-border hover:shadow-lg transition-shadow" data-testid={`stat-card-${index}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardDescription className="text-xs uppercase tracking-wider">{stat.title}</CardDescription>
                      <Icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                    <CardTitle className="text-3xl font-bold">{stat.value}</CardTitle>
                  </CardHeader>
                </Card>
              );
            })
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/admin/usuarios')}
            data-testid="nav-usuarios-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Usuarios</CardTitle>
                  <CardDescription>Gestionar usuarios y roles</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/admin/estructura')}
            data-testid="nav-estructura-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-secondary/10">
                  <Settings className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Estructura</CardTitle>
                  <CardDescription>Niveles, especialidades, RAs</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/admin/grupos')}
            data-testid="nav-grupos-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-[hsl(var(--accent-green))]/10">
                  <GraduationCap className="h-6 w-6" style={{ color: 'hsl(var(--accent-green))' }} />
                </div>
                <div>
                  <CardTitle className="text-lg">Grupos</CardTitle>
                  <CardDescription>Gestionar grupos y periodos</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/admin/revision')}
            data-testid="nav-revision-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-[hsl(var(--accent-yellow))]/10">
                  <FileCheck className="h-6 w-6" style={{ color: 'hsl(var(--accent-yellow))' }} />
                </div>
                <div>
                  <CardTitle className="text-lg">Revisión</CardTitle>
                  <CardDescription>Aprobar actividades</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardAdmin;
